import { createContext, useContext, type ReactNode } from "react";
import { createPortal } from "react-dom";

const PortalTargetContext = createContext<Element | DocumentFragment | null>(null);

/**
 * Tell every floating surface (Modal, Drawer, Menu, Tooltip, Toast) where to
 * render. The portal app can skip this (defaults to document.body). The
 * extension overlay MUST pass an element inside its shadow root so the
 * floating UI picks up the shadow-scoped styles.
 */
export function GfPortalProvider({ container, children }: { container: Element | DocumentFragment | null; children: ReactNode }) {
  return <PortalTargetContext.Provider value={container}>{children}</PortalTargetContext.Provider>;
}

export function usePortalTarget(): Element | DocumentFragment | null {
  const ctx = useContext(PortalTargetContext);
  if (ctx) return ctx;
  return typeof document === "undefined" ? null : document.body;
}

/** Render children into the configured portal target, inside a `.gf-layer` that carries base type/colour. */
export function GfPortal({ children }: { children: ReactNode }) {
  const target = usePortalTarget();
  if (!target) return null;
  return createPortal(<div className="gf-layer">{children}</div>, target);
}
